import { storage } from '../../utils/storage';
import { BaseApiService } from './baseApiService';

interface ChatMessage {
  role: string | undefined;
  transcript: string | undefined;
}

interface PatientInfo {
  name: string;
  age: number;
  gender: string;
}

interface PatientUpdateRequest {
  name: string;
  age: number;
  dateOfBirth: string;
  gender: string;
}

export interface AnzuTextTurn {
  role: string;
  content: string;
}

export interface AnzuTextMessagePayload {
  session_id: number;
  message: string;
  messages: AnzuTextTurn[];
  run_id?: string;
  patient_name: string;
  patient_age: string;
  patient_gender: string;
  specialty?: string;
}

export interface RealtimeUsagePayload {
  run_id: string;
  session_id: number | null;
  // gpt-realtime-2 — cumulative token counts
  input_text_tokens: number;
  output_text_tokens: number;
  input_audio_tokens: number;
  output_audio_tokens: number;
  // whisper-1 — cumulative transcription minutes
  audio_minutes: number;
}


class ChatService extends BaseApiService {
  async sendChatHistory(messages: ChatMessage[]): Promise<void> {
    const validMessages = messages.filter(msg => 
      msg.role !== undefined && msg.transcript !== undefined
    ).map(msg => ({
      role: msg.role as string,
      transcript: msg.transcript as string
    }));

    const authData = storage.getAuthData();
    const sessionId = authData?.session?.magic_link_session_id || authData?.session?.id;

    const encryptedData = this.encrypt({
      messages: validMessages,
      session_id: sessionId
    });

    await this.fetchWithAuth('/chat/message/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: encryptedData,
    });
  }

  async updatePatientInfo(data: PatientUpdateRequest): Promise<void> {
    try {
      const authData = storage.getAuthData();

      console.log("AUTH DATA",authData)
      if (!authData?.user?.id) {
        throw new Error('User ID not found');
      }

      const response = await this.fetchWithAuth(
        `/analysis/patient/${authData.user.id}/`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            preferred_name: data.name,
            age: data.age,
            date_of_birth: data.dateOfBirth,
            gender: data.gender,
          }),
        }
      );

      // Simple response check
      if (response.status !== 'success') {
        throw new Error(`API error: ${response.message || 'Unknown error'}`);
      }

    } catch (error) {
      console.error('Update patient info error:', error);
      // Don't throw the error, just log it
      // This prevents the error from propagating and causing a logout
    }
  }

  async sendAnzuTextMessage(payload: AnzuTextMessagePayload): Promise<any> {
    return this.fetchWithAuth('/chatbot/text/message/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });
  }

  async getChatbotSession(patientData: { patient_name: string; patient_age: string; patient_gender: string; practitioner_type?: string; communication_mode?: string; session_id?: number; sdp: string }): Promise<any> {
    return this.fetchWithAuth('/chatbot/session/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(patientData)
    });
  }

  /**
   * Report cumulative realtime voice usage (gpt-realtime-2 + whisper-1) for metering.
   * Best-effort: failures are swallowed so metering never disrupts the consultation.
   * Pass keepalive=true for the unload / session-end flush so it survives teardown.
   */
  async reportRealtimeUsage(payload: RealtimeUsagePayload, keepalive: boolean = false): Promise<void> {
    try {
      await this.fetchWithAuth('/chatbot/session/usage/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload),
        keepalive,
      });
    } catch (error) {
      console.warn('Realtime usage report failed (non-fatal):', error);
    }
  }
}

export const chatService = new ChatService(); 